import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormikProps, FormikProvider, useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';
import { Project } from 'src/modules/projects/domain';

import { Button, Label, RadioGroup, RadioGroupItem, Skeleton, Text } from '@/design-system/components/ui';
import { useScrollIntoErrors } from '@/hooks/useScrollIntoErrors';

import {
  EventType,
  INITIAL_UPDATE_TRIGGER_EXPRESSIONS_FORM_VALUES,
  Trigger,
  UpdateTriggerExpressionFormValues,
  updateTriggerExpressionsValidationSchema,
} from '../../../domain';
import { UpdateTriggerDTO } from '../../../infra/dtos';
import { mapToUpdateTriggerExpressions } from '../../../infra/encode';
import { mapToTrigger } from '../../../infra/mappers';
import { triggersService } from '../../../infra/triggersService';
import DefineTriggerArgumentExpression from './define-trigger-argument-expression';
import SelectTokenForCurrencyArgument from './select-token-for-trigger-argument';
import SelectTriggerArgument from './select-trigger-argument';

interface Props {
  trigger: Trigger;
  onSuccess?: () => void;
}

export default function UpdateTriggerExpressions({ trigger, onSuccess }: Props) {
  const queryClient = useQueryClient();
  const { project } = useProjectsContext();

  const { data: triggerDetail, isLoading } = useQuery({
    queryKey: ['trigger', trigger.id],
    queryFn: () => triggersService.getById(project?.id as string, trigger.id).then(mapToTrigger),
  });

  const updateTriggerExpressionsMutation = useMutation({
    mutationFn: (dto: UpdateTriggerDTO) => triggersService.update(project?.id as string, trigger.id, dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [project?.slug] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Error updating trigger expressions, please try again.');
    },
  });

  const hasVolumeExpression = !!trigger.volumeExpression;
  const hasRevenueExpression = !!trigger.revenueExpression;

  const chainId: number = Number(trigger.contracts![0].chainId);

  const requires = {
    requireVolume: !hasVolumeExpression,
    requireRevenue: !hasRevenueExpression,
  };

  const form: FormikProps<UpdateTriggerExpressionFormValues> = useFormik({
    initialValues: INITIAL_UPDATE_TRIGGER_EXPRESSIONS_FORM_VALUES,
    validationSchema: updateTriggerExpressionsValidationSchema(requires),
    onSubmit: async (formValues) => {
      const dto = mapToUpdateTriggerExpressions(project as Project, trigger.id, formValues);

      updateTriggerExpressionsMutation.mutate(dto);
    },
  });

  const { values, touched, errors, setFieldValue, handleSubmit, isSubmitting } = form;

  useScrollIntoErrors([isSubmitting]);

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Skeleton variant="text" width="45%" />
        <Skeleton variant="text" width="65%" />
      </div>
    );
  }

  if (!triggerDetail) {
    return (
      <Text variant="body-md" color="muted">
        No trigger found
      </Text>
    );
  }

  const getTypeText = () => {
    return triggerDetail.type === EventType.OnChainFunction ? 'function' : 'event';
  };

  const contract = triggerDetail.contracts?.[0];
  const triggerFunctionOrEvent =
    triggerDetail.type === EventType.OnChainFunction
      ? contract?.functions.find((func) => func.signature === triggerDetail.signature)
      : contract?.events.find((event) => event.signature === triggerDetail.signature);
  const availableAddressArguments = triggerFunctionOrEvent?.inputs.filter((input) => input.type.includes('address'));

  if (!triggerFunctionOrEvent) {
    return (
      <Text variant="body-md" color="muted">
        No function or event found for this trigger
      </Text>
    );
  }

  if (!contract) {
    return (
      <Text variant="body-md" color="muted">
        No contract found for this trigger
      </Text>
    );
  }

  return (
    <FormikProvider value={form}>
      <div className="tw:flex tw:flex-col tw:gap-6">
        {!hasVolumeExpression && (
          <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4">
            <div
              className={`tw:flex tw:flex-col tw:gap-2 ${touched.volumeArgument?.type && !!errors.volumeArgument?.type ? 'tw:text-red-500' : ''}`}
            >
              <Text variant="body-sm">Define how to get the volume ($) of the transaction</Text>
              <RadioGroup
                name="volumeArgument.type"
                value={values.volumeArgument.type ?? ''}
                onValueChange={(val) => setFieldValue('volumeArgument.type', val)}
              >
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromTriggerArgument" id="upd-vol-fromTriggerArgument" />
                  <Label htmlFor="upd-vol-fromTriggerArgument">
                    <Text variant="body-sm" as="span">
                      The transaction volume comes from an argument of the {getTypeText()}
                    </Text>
                  </Label>
                </div>
                {values.volumeArgument.type === 'fromTriggerArgument' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTriggerArgument
                      inputName="volumeArgument"
                      selectedFunctionOrEvent={triggerFunctionOrEvent}
                      type={trigger.type}
                    />
                  </div>
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromCalculationBasedOnTriggerArguments" id="upd-vol-fromCalculation" />
                  <Label htmlFor="upd-vol-fromCalculation">
                    <Text variant="body-sm" as="span">
                      The transaction volume comes from a calculation using {getTypeText()} arguments
                    </Text>
                  </Label>
                </div>
                {values.volumeArgument.type === 'fromCalculationBasedOnTriggerArguments' && (
                  <DefineTriggerArgumentExpression
                    inputName="volumeArgument"
                    contract={contract}
                    selectedFunctionOrEvent={triggerFunctionOrEvent}
                    type={trigger.type}
                  />
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="noValueAssociated" id="upd-vol-noValueAssociated" />
                  <Label htmlFor="upd-vol-noValueAssociated">
                    <Text variant="body-sm" as="span">
                      This transaction has no volume associated
                    </Text>
                  </Label>
                </div>
              </RadioGroup>
              {touched.volumeArgument?.type && !!errors.volumeArgument?.type && (
                <p className="tw:text-sm tw:text-red-500">{errors?.volumeArgument.type}</p>
              )}
            </div>
          </div>
        )}

        {!hasVolumeExpression && (
          <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4">
            <div
              className={`tw:flex tw:flex-col tw:gap-2 ${touched.currencyVolumeArgument?.type && !!errors.currencyVolumeArgument?.type ? 'tw:text-red-500' : ''}`}
            >
              <Text variant="body-sm">Define how to get the currency of the volume of the transaction</Text>
              <RadioGroup
                name="currencyVolumeArgument.type"
                value={values.currencyRevenueArgument.type ?? ''}
                onValueChange={(val) => setFieldValue('currencyVolumeArgument.type', val)}
              >
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromToken" id="upd-cur-vol-fromToken" />
                  <Label htmlFor="upd-cur-vol-fromToken">
                    <Text variant="body-sm" as="span">
                      The transaction currency is always the same token
                    </Text>
                  </Label>
                </div>
                {values.currencyVolumeArgument.type === 'fromToken' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTokenForCurrencyArgument inputName="currencyVolumeArgument" {...{ chainId }} />
                  </div>
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem
                    value="fromTriggerArgument"
                    id="upd-cur-vol-fromTriggerArgument"
                    disabled={!availableAddressArguments?.length}
                  />
                  <Label htmlFor="upd-cur-vol-fromTriggerArgument">
                    <Text variant="body-sm" as="span">
                      The transaction currency comes from an argument of the {getTypeText()}
                    </Text>
                  </Label>
                </div>
                {values.currencyVolumeArgument.type === 'fromTriggerArgument' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTriggerArgument
                      inputName="currencyVolumeArgument"
                      selectedFunctionOrEvent={triggerFunctionOrEvent}
                      type={trigger.type}
                    />
                  </div>
                )}
              </RadioGroup>
              {touched.currencyVolumeArgument?.type && !!errors.currencyVolumeArgument?.type && (
                <p className="tw:text-sm tw:text-red-500">{errors?.currencyVolumeArgument.type}</p>
              )}
            </div>
          </div>
        )}

        {!hasRevenueExpression && (
          <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4">
            <div
              className={`tw:flex tw:flex-col tw:gap-2 ${touched.revenueArgument?.type && !!errors.revenueArgument?.type ? 'tw:text-red-500' : ''}`}
            >
              <Text variant="body-sm">
                Define how to calculate your revenue (if any) from of the contract interaction
              </Text>
              <RadioGroup
                name="revenueArgument.type"
                value={values.revenueArgument.type ?? ''}
                onValueChange={(val) => setFieldValue('revenueArgument.type', val)}
              >
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromTriggerArgument" id="upd-rev-fromTriggerArgument" />
                  <Label htmlFor="upd-rev-fromTriggerArgument">
                    <Text variant="body-sm" as="span">
                      The transaction revenue comes from an argument of the {getTypeText()}
                    </Text>
                  </Label>
                </div>
                {values.revenueArgument.type === 'fromTriggerArgument' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTriggerArgument
                      inputName="revenueArgument"
                      selectedFunctionOrEvent={triggerFunctionOrEvent}
                      type={trigger.type}
                    />
                  </div>
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromCalculationBasedOnTriggerArguments" id="upd-rev-fromCalculation" />
                  <Label htmlFor="upd-rev-fromCalculation">
                    <Text variant="body-sm" as="span">
                      The transaction revenue comes from a calculation using {getTypeText()} arguments
                    </Text>
                  </Label>
                </div>
                {values.revenueArgument.type === 'fromCalculationBasedOnTriggerArguments' && (
                  <DefineTriggerArgumentExpression
                    inputName="revenueArgument"
                    contract={contract}
                    selectedFunctionOrEvent={triggerFunctionOrEvent}
                    type={trigger.type}
                  />
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="noRevenueAssociated" id="upd-rev-noRevenueAssociated" />
                  <Label htmlFor="upd-rev-noRevenueAssociated">
                    <Text variant="body-sm" as="span">
                      This transaction has no revenue associated
                    </Text>
                  </Label>
                </div>
              </RadioGroup>
              {touched.revenueArgument?.type && !!errors.revenueArgument?.type && (
                <p className="tw:text-sm tw:text-red-500">{errors?.revenueArgument.type}</p>
              )}
            </div>
          </div>
        )}

        {!hasRevenueExpression && (
          <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4">
            <div
              className={`tw:flex tw:flex-col tw:gap-2 ${touched.currencyRevenueArgument?.type && !!errors.currencyRevenueArgument?.type ? 'tw:text-red-500' : ''}`}
            >
              <Text variant="body-sm">Define how to get the currency of the revenue of the transaction</Text>
              <RadioGroup
                name="currencyRevenueArgument.type"
                value={values.currencyRevenueArgument.type ?? ''}
                onValueChange={(val) => setFieldValue('currencyRevenueArgument.type', val)}
              >
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem value="fromToken" id="upd-cur-rev-fromToken" />
                  <Label htmlFor="upd-cur-rev-fromToken">
                    <Text variant="body-sm" as="span">
                      The transaction currency is always the same token
                    </Text>
                  </Label>
                </div>
                {values.currencyRevenueArgument.type === 'fromToken' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTokenForCurrencyArgument inputName="currencyRevenueArgument" {...{ chainId }} />
                  </div>
                )}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <RadioGroupItem
                    value="fromTriggerArgument"
                    id="upd-cur-rev-fromTriggerArgument"
                    disabled={!availableAddressArguments?.length}
                  />
                  <Label htmlFor="upd-cur-rev-fromTriggerArgument">
                    <Text variant="body-sm" as="span">
                      The transaction currency comes from an argument of the {getTypeText()}
                    </Text>
                  </Label>
                </div>
                {values.currencyRevenueArgument.type === 'fromTriggerArgument' && (
                  <div className="tw:py-1 tw:min-w-[50%]">
                    <SelectTriggerArgument
                      inputName="currencyRevenueArgument"
                      selectedFunctionOrEvent={triggerFunctionOrEvent}
                      type={trigger.type}
                    />
                  </div>
                )}
              </RadioGroup>
              {touched.currencyRevenueArgument?.type && !!errors.currencyRevenueArgument?.type && (
                <p className="tw:text-sm tw:text-red-500">{errors?.currencyRevenueArgument.type}</p>
              )}
            </div>
          </div>
        )}
        <Button variant="primary" onClick={() => handleSubmit()} isLoading={updateTriggerExpressionsMutation.isPending}>
          Save
        </Button>
      </div>
    </FormikProvider>
  );
}
